import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { useLanguage } from '@/contexts/LanguageContext';
import { Bot, Zap, TrendingUp, Shield, CheckCircle, ArrowRight, Clock, Target, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const AgenticAI = () => {
  const { t } = useLanguage();
  
  useSEO({
    title: "Agentic AI Solutions | Autonomous AI Agents for Business",
    description: "Transform your business with autonomous Agentic AI agents. Independent task execution, 40% efficiency gains, enterprise-ready solutions. Get your free consultation.",
    keywords: "agentic AI, autonomous AI agents, independent AI systems, AI automation, enterprise AI agents, business AI transformation, agentic AI consulting, autonomous systems",
    ogTitle: "Agentic AI Solutions | Autonomous AI Agents for Business",
    ogDescription: "Deploy autonomous AI agents that work independently. 40% efficiency gains, reduced human oversight, enterprise-grade Agentic AI solutions.",
    canonical: "https://quantumbox.ai/agentic-ai"
  });

  const benefits = [
    {
      icon: Bot,
      title: "Autonomous Task Execution",
      description: "AI agents work independently without constant human supervision",
      metric: "90% less oversight needed"
    },
    {
      icon: Zap,
      title: "Real-Time Decision Making",
      description: "Instant responses to business situations and customer needs",
      metric: "85% faster response times"
    },
    {
      icon: TrendingUp,
      title: "Continuous Learning",
      description: "Agents improve performance through experience and feedback",
      metric: "25% monthly improvement"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Built-in compliance and security protocols for business data",
      metric: "99.9% security compliance"
    }
  ];

  const useCases = [
    {
      industry: "Customer Service",
      title: "Autonomous Support Agents",
      description: "AI agents handle complex customer inquiries, escalate when needed, and learn from interactions",
      results: ["35% reduction in support costs", "24/7 availability", "90% customer satisfaction"]
    },
    {
      industry: "Sales Operations",
      title: "Intelligent Lead Qualification",
      description: "Agents autonomously qualify leads, schedule meetings, and nurture prospects through the funnel",
      results: ["45% more qualified leads", "60% faster qualification", "30% higher conversion"]
    },
    {
      industry: "Data Analysis",
      title: "Autonomous Business Intelligence",
      description: "AI agents continuously monitor KPIs, generate reports, and provide actionable insights",
      results: ["Real-time insights", "50% faster reporting", "Predictive trend analysis"]
    }
  ];

  const implementation = [
    {
      step: 1,
      title: "Agent Design & Planning",
      description: "Define autonomous agent capabilities and business objectives",
      timeline: "Week 1-2"
    },
    {
      step: 2,
      title: "Infrastructure Setup",
      description: "Deploy secure, scalable agent execution environment",
      timeline: "Week 2-3"
    },
    {
      step: 3,
      title: "Agent Training",
      description: "Train agents on your specific business processes and data",
      timeline: "Week 3-4"
    },
    {
      step: 4,
      title: "Autonomous Deployment",
      description: "Launch agents with monitoring and continuous improvement",
      timeline: "Week 4-5"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-secondary text-white border-0">
              #1 Trending AI Technology 2025
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Agentic AI Solutions
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
              Deploy autonomous AI agents that work independently, make decisions, and execute tasks without human oversight. 
              The future of business automation is here.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button size="lg" asChild className="bg-gradient-to-r from-primary to-secondary hover:scale-105 transition-transform">
                <Link to="/assessment">Get Free AI Agent Assessment</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/contact">Schedule Demo</Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">40%</div>
                <div className="text-sm text-muted-foreground">Efficiency Increase</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-secondary">90%</div>
                <div className="text-sm text-muted-foreground">Less Human Oversight</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent">24/7</div>
                <div className="text-sm text-muted-foreground">Autonomous Operation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Agentic AI Outperforms Traditional Automation</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Unlike basic automation, Agentic AI agents think, decide, and act independently
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="relative overflow-hidden group hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20">
                      <benefit.icon className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{benefit.title}</CardTitle>
                      <Badge variant="secondary" className="mt-1">{benefit.metric}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Agentic AI in Action</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Real-world applications delivering measurable business impact
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <Card key={index} className="relative overflow-hidden group hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <Badge className="w-fit mb-2">{useCase.industry}</Badge>
                  <CardTitle className="text-xl">{useCase.title}</CardTitle>
                  <CardDescription>{useCase.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {useCase.results.map((result, resultIndex) => (
                      <div key={resultIndex} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{result}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">5-Week Agentic AI Implementation</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From concept to autonomous operation in just 5 weeks
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            {implementation.map((phase, index) => (
              <div key={index} className="flex items-start gap-6 mb-8 last:mb-0">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold">
                  {phase.step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h3 className="text-xl font-semibold">{phase.title}</h3>
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {phase.timeline}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Deploy Autonomous AI Agents?</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Join leading enterprises leveraging Agentic AI for competitive advantage. 
            Get your free assessment and implementation roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-secondary hover:scale-105 transition-transform">
              <Link to="/assessment">
                Start Free Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contact">Book Consultation</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgenticAI;